/**
 * Mobile Consultant Invoice — submit a service invoice with a photo/PDF.
 *
 * Backend: POST /api/hr/consultant-invoices (multipart) — Change 19.
 * Only visible when the logged-in employee has employment_type === 'Consultant'.
 * Flows into the Approvals Hub for HR/Finance approval.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Send, Loader2, Camera, X } from 'lucide-react';
import { api, apiGet } from '../../lib/api';

export default function MobileConsultantInvoice() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    description: '',
    amount: '',
    invoice_number: '',
    period_covered: '',
  });

  // Confirm the logged-in user is actually a consultant — if not, redirect home
  useEffect(() => {
    apiGet('/auth/me').then(setMe).catch(() => {});
  }, []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25 MB)');
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setFilePreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setFilePreview('');
    }
  };

  const submit = async () => {
    if (!form.description.trim()) return toast.error('Description is required');
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Valid amount is required');
    if (!file) return toast.error('Please attach the invoice (photo or PDF)');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', form.description.trim());
      fd.append('amount', String(Number(form.amount)));
      if (form.invoice_number.trim()) fd.append('invoice_number', form.invoice_number.trim());
      if (form.period_covered.trim()) fd.append('period_covered', form.period_covered.trim());
      const { data } = await api.post('/hr/consultant-invoices', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Invoice submitted — ID ${data.id?.slice(0, 8)}`);
      nav('/m/home');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="mobile-consultant-invoice" className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-[#1F7A6E] text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
        <Link to="/m/home" className="p-1 hover:bg-white/10 rounded-sm" data-testid="back-home">
          <ArrowLeft size={20} />
        </Link>
        <FileText size={20} />
        <div className="flex-1">
          <div className="font-display font-bold text-base">Submit Invoice</div>
          <div className="text-[10px] text-white/70">Goes to HR/Finance for approval</div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Description <span className="text-red-500">*</span></span>
          <textarea data-testid="invoice-description"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="e.g. Consulting services — June 2026"
            rows={2}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Amount (₹) <span className="text-red-500">*</span></span>
            <input data-testid="invoice-amount" type="number" min="1"
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="50000"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Invoice No.</span>
            <input data-testid="invoice-number" value={form.invoice_number}
              onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
              placeholder="INV-2026-001"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Period Covered</span>
          <input data-testid="invoice-period" value={form.period_covered}
            onChange={e => setForm(f => ({ ...f, period_covered: e.target.value }))}
            placeholder="June 2026 / Q2 2026 / Milestone 1"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </label>

        <div>
          <span className="text-sm font-medium text-slate-700">Invoice Attachment <span className="text-red-500">*</span></span>
          {!file ? (
            <label className="mt-2 block border-2 border-dashed border-slate-300 rounded-sm p-6 text-center cursor-pointer hover:bg-slate-50">
              <Camera size={28} className="mx-auto text-slate-400" />
              <p className="text-sm text-slate-600 mt-2">Tap to attach photo / PDF</p>
              <p className="text-[10px] text-slate-400 mt-1">JPG, PNG, PDF — max 25 MB</p>
              <input data-testid="invoice-file" type="file" accept="image/*,application/pdf"
                capture="environment"
                onChange={pickFile} className="hidden" />
            </label>
          ) : (
            <div className="mt-2 border border-slate-300 rounded-sm p-3 bg-white">
              <div className="flex items-center gap-3">
                {filePreview ? (
                  <img src={filePreview} alt="preview" className="w-16 h-16 object-cover rounded-sm" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded-sm flex items-center justify-center">
                    <FileText size={24} className="text-slate-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</div>
                </div>
                <button data-testid="clear-file" onClick={() => { setFile(null); setFilePreview(''); }}
                  className="p-1 hover:bg-slate-100 rounded-sm text-red-500">
                  <X size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        <button data-testid="invoice-submit" onClick={submit} disabled={submitting}
          className="w-full py-4 bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {submitting ? 'Submitting…' : 'Submit Invoice'}
        </button>

        <p className="text-[11px] text-slate-500 leading-snug">
          Once submitted, your invoice appears in the Approvals Hub and is reviewed
          by HR/Finance. You'll see status updates here on your phone.
        </p>
      </div>
    </div>
  );
}
